// ────────────────────────────────────────────────────────────────────
// The identity block: who the asset is, what it is judged under, and the four
// right-aligned facts the handoff hangs beside it
// (design_handoff/README.md:158-160).
//
// TWO SOURCES, and the split is not an accident. The integrity record supplies
// the authority, the regulation and every method ever run on the asset. It
// does NOT carry the asset's in-service date, geometry, nominal wall or CML
// count — those live on the asset row (`GET /api/assets/{id}`), which the
// screen already holds. `AssetIdentityFacts` is a structural SUBSET of
// `lib/types.ts` Asset, so a caller passes its Asset straight in.
// ────────────────────────────────────────────────────────────────────
import { isoDay, mm, EMDASH } from "./format";
import { oldestFirst } from "./order";
import type { AssetIntegrity } from "./types";

/** The asset-row fields this block prints. Deliberately narrow: a subset of
 *  `Asset`, so `<AssetIdentityBlock asset={asset} …/>` type-checks with the
 *  whole row and this file never grows a second copy of it. */
export interface AssetIdentityFacts {
  tag: string;
  name: string;
  asset_class: string;
  service: string;
  material: string;
  first_service_date: string;
  diameter_mm: number | null;
  height_mm: number | null;
  length_mm: number | null;
  t_nominal_mm: number | null;
  cml_count: number;
}

const RULE = "1px solid var(--line-2)";

/** Whole millimetres with thousands separators — a 42 m line reads as
 *  42,000 mm, not 42000. Presentation only. */
function bigMm(v: number | null): string | null {
  return v == null ? null : Math.round(v).toLocaleString("en-US");
}

/** Whatever geometry the asset row actually holds, in the order a drawing
 *  reads it. Nothing is inferred: a missing dimension is simply absent, never
 *  back-calculated from capacity. */
function geometry(a: AssetIdentityFacts): string {
  const d = bigMm(a.diameter_mm);
  const span = bigMm(a.height_mm) ?? bigMm(a.length_mm);
  const parts: string[] = [];
  if (d) parts.push(`⌀ ${d}`);
  if (span) parts.push(span);
  return parts.length ? `${parts.join(" × ")} mm` : EMDASH;
}

/** Every method ever run on this asset, each named once, in the order the
 *  asset met them — OLDEST INSPECTION FIRST, which the wire is not (it is
 *  newest first, see ./order). Asked for explicitly: iterating the record's own
 *  order named the newest inspection's methods first while this line claimed
 *  the opposite.
 *  De-duplication, not a calculation — the methods arrive per inspection. */
function methodsRun(record: AssetIntegrity): string[] {
  const seen: string[] = [];
  for (const ev of oldestFirst(record.inspections)) {
    for (const m of ev.methods) if (!seen.includes(m)) seen.push(m);
  }
  return seen;
}

function Fact({ id, label, value }: { id: string; label: string; value: string }): JSX.Element {
  return (
    <div style={{ textAlign: "right", paddingLeft: 18, borderLeft: RULE }}>
      <div className="font-mono" style={{ fontSize: 9.5, letterSpacing: ".09em", color: "var(--ink-4)" }}>
        {label}
      </div>
      <div data-testid={`fact-${id}`} className="font-mono"
           style={{ fontSize: 12.5, color: "var(--ink-1)", marginTop: 3 }}>
        {value}
      </div>
    </div>
  );
}

export function AssetIdentityBlock({ asset, record }: {
  asset: AssetIdentityFacts;
  record: AssetIntegrity;
}): JSX.Element {
  const methods = methodsRun(record);
  return (
    <section data-testid="asset-identity"
             style={{
               display: "flex", alignItems: "flex-end", justifyContent: "space-between",
               gap: 24, flexWrap: "wrap",
               padding: "18px 0 16px", borderBottom: RULE,
             }}>
      <div style={{ minWidth: 240 }}>
        <div className="font-mono" style={{ fontSize: 19, fontWeight: 600, color: "var(--ink-1)", letterSpacing: "-.01em" }}>
          {asset.tag}
        </div>
        <div style={{ fontSize: 13.5, color: "var(--ink-2)", marginTop: 3 }}>
          {asset.name}
        </div>
        <div className="font-mono" style={{ fontSize: 10.5, color: "var(--ink-3)", marginTop: 6, letterSpacing: ".05em" }}>
          {[asset.asset_class.toUpperCase(), asset.service, asset.material]
            .filter(Boolean).join("  ·  ")}
        </div>
        <div className="font-mono" style={{ fontSize: 10.5, color: "var(--accent-deep)", marginTop: 4, letterSpacing: ".05em" }}>
          {/* Who says so. Both come from the record, not from this file. */}
          {[record.authority, record.regulation].filter(Boolean).join("  ·  ")}
        </div>
      </div>

      <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
        <Fact id="in_service"     label="IN SERVICE"   value={isoDay(asset.first_service_date)} />
        <Fact id="geometry"       label="GEOMETRY"     value={geometry(asset)} />
        <Fact id="nominal_wall"   label="NOMINAL WALL"
              value={asset.t_nominal_mm == null ? EMDASH : `${mm(asset.t_nominal_mm)} mm`} />
        <Fact id="points_methods" label="POINTS · METHODS"
              value={`${asset.cml_count} points · ${methods.length ? methods.join(" · ") : "none run"}`} />
      </div>
    </section>
  );
}
